import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import React, { ReactNode } from 'react';
import ProcessInfoIcon from '../ProcessInfoIcon';

const getPointyStyles = () => ({
  content: '""',
  position: 'absolute',
  right: '-15px',
  bottom: 'calc(50% - 15px)',
  width: '0',
  height: '0',
  borderLeftColor: 'inherit',
  borderLeft: '15px solid',
  borderTop: '15px solid transparent',
  borderBottom: '15px solid transparent'
});

const PREFIX = 'Phase';

const classes = {
  container: `${PREFIX}-container`,
  pointerCard: `${PREFIX}-pointerCard`,
  card: `${PREFIX}-card`,
  selectedCard: `${PREFIX}-selectedCard`,
  idleCard: `${PREFIX}-idleCard`,
  idleCardFirstLevel: `${PREFIX}-cardFirstLevel`,
  selectedCardFirstLevel: `${PREFIX}-selectedCardFirstLevel`,
  contentWrapper: `${PREFIX}-contentWrapper`,
  scrollContainer: `${PREFIX}-scrollContainer`,
  numberContainer: `${PREFIX}-numberContainer`,
  numberWrapper: `${PREFIX}-numberWrapper`,
  centerContent: `${PREFIX}-centerContent`
};

const Root = styled('div')(({ theme }) => ({
  [`&.${classes.container}`]: {
    marginRight: '20px',
    marginLeft: '20px',
    flex: '1 1 0',
    minWidth: '96px',
    maxWidth: '236px',
    height: '108px',
    display: 'flex',
    alignItems: 'flex-end'
  },

  [`& .${classes.card}`]: {
    outline: 0,
    position: 'relative',
    height: '82px',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '&:hover ': {
      '.ProcessInfo-infoIconDiv': {
        visibility: 'visible'
      }
    }
  },
  [`& .${classes.idleCardFirstLevel}`]: {
    background: theme.palette.info.light,
    borderColor: theme.palette.info.light,
    '&:before': {
      color: theme.palette.info.light
    },
    '&:hover': {
      background: theme.palette.info.main,
      borderColor: theme.palette.info.main
    },
    '&:hover:before': {
      color: theme.palette.info.main
    }
  },
  [`& .${classes.idleCard}`]: {
    background: theme.palette.info.light,
    borderColor: theme.palette.info.light,
    '&:before': {
      color: theme.palette.info.light
    },
    '&:hover': {
      background: theme.palette.primary.light,
      borderColor: theme.palette.primary.light
    },
    '&:hover:before': {
      color: theme.palette.primary.light
    }
  },
  [`& .${classes.selectedCard}`]: {
    background: theme.palette.primary.light,
    borderColor: theme.palette.primary.light,
    '&:before': {
      color: theme.palette.primary.light
    }
  },
  [`& .${classes.pointerCard}`]: {
    '&::before': getPointyStyles()
  },

  [`& .${classes.selectedCardFirstLevel}`]: {
    background: theme.palette.info.main,
    borderColor: theme.palette.info.main,
    '&:before': {
      color: theme.palette.info.main
    }
  },

  [`& .${classes.contentWrapper}`]: {
    display: 'flex',
    height: '72px'
  },

  [`& .${classes.scrollContainer}`]: {
    margin: 'auto',
    maxHeight: '100%',
    overflow: 'auto',
    '&::-webkit-scrollbar': {
      width: '7px'
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#888',
      borderRadius: '3px'
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#555'
    },
    '&::-webkit-scrollbar-track': {
      background: '#f1f1f1',
      borderRadius: '3px'
    },
    scrollbarColor: '#888 #f1f1f1',
    scrollbarWidth: 'thin'
  },

  [`& .${classes.numberContainer}`]: {
    top: '-20px',
    left: '-20px',
    position: 'absolute',
    zIndex: 1
  },

  [`& .${classes.numberWrapper}`]: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: theme.palette.info.dark,
    color: theme.palette.primary.contrastText
  },

  [`& .${classes.centerContent}`]: {
    flex: '0 0 '
  }
}));

interface PointerCardProps {
  children?: ReactNode;
  number: number;
  last?: boolean;
  isSelected: boolean;
  firstLevel: boolean;
  onInfoIconClick: () => void;
}

const getCardClasses = (isSelected: boolean, last: boolean, firstLevel: boolean) => {
  let cardClasses = classes.card;

  if (isSelected) {
    if (firstLevel) {
      cardClasses += ' ';
      cardClasses += classes.selectedCardFirstLevel;
    } else {
      cardClasses += ' ';
      cardClasses += classes.selectedCard;
    }
  } else {
    cardClasses += ' ';
    if (firstLevel) {
      cardClasses += classes.idleCardFirstLevel;
    } else {
      cardClasses += classes.idleCard;
    }
  }

  if (!last) {
    cardClasses += ' ';
    cardClasses += classes.pointerCard;
  }

  return cardClasses;
};

const Phase = ({
  children,
  number,
  last = false,
  isSelected,
  firstLevel,
  onInfoIconClick,
  ...props
}: PointerCardProps) => {
  return (
    <Root className={classes.container}>
      <Paper className={getCardClasses(isSelected, last, firstLevel)}>
        {<ProcessInfoIcon onClick={onInfoIconClick} />}
        <div className={classes.numberContainer}>
          <div className={classes.numberWrapper}>
            <div className={classes.centerContent}>{number}</div>
          </div>
        </div>
        <div {...props} data-testid="process-phase">
          <div className={classes.contentWrapper}>
            <div className={classes.scrollContainer}>
              <Typography variant="subtitle2">{children}</Typography>
            </div>
          </div>
        </div>
      </Paper>
    </Root>
  );
};

export default Phase;
